import Head from 'next/head';
import {useRouter} from 'next/router';

import {HeadSEO, useCanonicalUrl} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {META_DEFAULT_BASE_URL} from '@udemy/nextjs-core';
import {getProfessionalSkillsShowcaseDataByLocale} from '@udemy/react-discovery-units';
import {SitelinkSearchBoxScript} from '@udemy/schema-markup';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {getLayout} from 'src/components/layout';

import {ProfessionalSkillsShowcaseDataProvider} from './contexts/professional-skills-showcase-data-context';
import {
    LoggedOutHomePageBody,
    LoggedOutHomePageBodyProps,
} from './logged-out-homepage-body.react-component';

const MX_SEARCH_URL = 'courses/search';

export function LoggedOutHomePageFeature(props: LoggedOutHomePageBodyProps) {
    const {Config: udConfig, request} = useUDData();
    const router = useRouter();
    const {gettext} = useI18n();
    const pageTitle = gettext('Udemy: Online Courses for Skills, Careers & AI');
    const pageDescription = gettext(
        'Learn in-demand skills with online courses, get professional certificates that advance your career, and explore courses in AI, coding, business and more.',
    );
    const {url: canonicalUrl} = useCanonicalUrl(
        // explicitly set asPath to '/' to avoid the issue where the canonical URL
        // picks up the nextjs '/lohp' path which is only used for local development
        {...router, asPath: '/'} as {locale: string; asPath: string},
        [],
        META_DEFAULT_BASE_URL,
    );

    const psuData = getProfessionalSkillsShowcaseDataByLocale(request.language);

    return (
        <>
            <SitelinkSearchBoxScript
                brandUrl={udConfig.url.to_root}
                searchHandlerUri={`${udConfig.url.to_root}${MX_SEARCH_URL}`}
                headWrapper={Head}
            />
            <HeadSEO
                title={pageTitle}
                description={pageDescription}
                ogTitle={pageTitle}
                ogDescription={pageDescription}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <SubscriptionPlansProvider
                subscriptionPlanQueryVariables={[
                    {
                        filters: {
                            productType: SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
                        },
                    },
                    {
                        filters: {productType: SubscriptionPlanProductType.Team},
                    },
                    {
                        filters: {productType: SubscriptionPlanProductType.GrowWithGoogle},
                    },
                    {
                        filters: {
                            productType:
                                SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                        },
                    },
                ]}
            >
                <ProfessionalSkillsShowcaseDataProvider context={psuData}>
                    <LoggedOutHomePageBody {...props} />
                </ProfessionalSkillsShowcaseDataProvider>
            </SubscriptionPlansProvider>
        </>
    );
}

LoggedOutHomePageFeature.getLayout = getLayout;
